import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { APP_ROUTES } from "@/config/appConfig";
import { useAuthSession } from "@/contexts/AuthSessionContext";
import { cn } from "@/lib/utils";
import { CalendarDays, CircleHelp, ClipboardList, LogOut } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import logoUrl from "@/assets/svg/logo.svg";

const navItems = [
  {
    to: APP_ROUTES.PORTAL.VIEWINGS,
    labelId: "layout.nav.viewings",
    icon: ClipboardList,
  },
  {
    to: APP_ROUTES.PORTAL.VACATION,
    labelId: "layout.nav.vacation",
    icon: CalendarDays,
  },
  {
    to: APP_ROUTES.PORTAL.HELP,
    labelId: "layout.nav.help",
    icon: CircleHelp,
  },
] as const;

function PortalNav({ compact = false }: { compact?: boolean }) {
  const intl = useIntl();

  return (
    <nav
      aria-label={intl.formatMessage({ id: "layout.nav.label" })}
      className={cn(
        compact ? "flex gap-2 overflow-x-auto" : "flex flex-col gap-1",
      )}
    >
      {navItems.map(({ to, labelId, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) =>
            cn(
              "inline-flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors",
              isActive
                ? "bg-sidebar-accent text-primary"
                : "text-primary hover:bg-muted hover:text-primary",
            )
          }
        >
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <FormattedMessage id={labelId} />
        </NavLink>
      ))}
    </nav>
  );
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function AuthenticatedInspectorPortalLayout() {
  const { session, logout } = useAuthSession();

  if (!session) {
    return null;
  }

  const inspectorName = session.fullName || session.inspectorId;

  return (
    <div
      data-testid="inspector-dashboard-shell"
      className="min-h-screen bg-background text-primary"
    >
      <aside className="fixed inset-y-0 left-0 hidden w-[24.25rem] border-r border-sidebar-border bg-card text-sidebar-foreground lg:flex lg:flex-col">
        <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-5">
          <div className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-primary h-14">
            <NavLink to={APP_ROUTES.ROOT}>
              <img src={logoUrl} alt="Evalion" className="h-4 cursor-pointer" />
            </NavLink>
          </div>
        </div>

        <div className="flex-1 px-5 py-4">
          <PortalNav />
        </div>

        <div className="border-t border-sidebar-border p-5">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-sm font-semibold text-primary-foreground">
              {getInitials(inspectorName)}
            </div>
            <div>
              <p className="text-sm font-semibold text-primary">
                {inspectorName}
              </p>
              <p className="text-sm text-muted-foreground">
                <FormattedMessage id="layout.brand.subtitle" />
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={logout}
          >
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            <FormattedMessage id="layout.logout" />
          </Button>
        </div>
      </aside>

      <div className="lg:pl-[24.25rem]">
        <header className="border-b border-sidebar-border bg-card px-4 py-2 md:px-6">
          <div className="flex min-h-10 flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-base font-semibold tracking-tight text-primary">
                <FormattedMessage id="layout.title" />
              </h1>
              <p className="text-xs text-muted-foreground">
                <FormattedMessage id="layout.lastUpdated" />
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <LanguageSelector />
              <div className="flex h-10 min-w-10 items-center justify-center rounded-md bg-sidebar-accent px-3 text-sm font-semibold text-primary">
                {getInitials(inspectorName)}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="lg:hidden"
                onClick={logout}
              >
                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                <FormattedMessage id="layout.logout" />
              </Button>
            </div>
          </div>
          <div className="mt-4 lg:hidden">
            <PortalNav compact />
          </div>
        </header>

        <main className="min-h-[calc(100vh-57px)] bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function InspectorPortalLayout() {
  const { isSessionLoading, session } = useAuthSession();
  const location = useLocation();

  if (isSessionLoading) {
    return null;
  }

  if (!session) {
    return (
      <Navigate to={APP_ROUTES.AUTH.LOGIN} replace state={{ from: location }} />
    );
  }

  return <AuthenticatedInspectorPortalLayout />;
}
